// The scan archive on the phone.
//
// Every scan is written here the instant it is read, before anything is sent. This
// is the part that decides whether the app works on a hall floor: venue WiFi at a
// coffee station is the worst in the building, and nobody is standing there to
// notice that nothing is going through.
//
// Lead scans and door scans both live here, and are counted and listed together: the
// panel and a support request answer for the phone as a whole.
//
// IndexedDB rather than simple storage, which Safari caps at roughly 5 MB and which
// fails by throwing once full. A scan is about 200 bytes, and an installed home
// screen app is granted a far larger allowance, so the size is not a real limit.
// The failure it avoids is the silent one.
//
// Nothing is ever removed as a side effect. A successful upload marks a record, a
// rejection marks a record, and app updates and reloads leave everything alone.
// Records leave only through a wipe or the retention ceiling.
//
// Every record carries the pairing it was taken under, and every read is asked for one
// pairing. The wipe and the retention ceiling stay blind to it: both answer for the
// whole phone.
import { LEGACY_PAIRING } from './scanPairing.ts'

// unrecorded is the door's: a check taken with no connection that nobody decided on.
// Nothing is waiting to be sent for it, so it must not sit in the pending count.
export type ScanStatus = 'pending' | 'synced' | 'rejected' | 'unrecorded'

export type ScanKind = 'lead' | 'door'

// What the phone did with a badge at a door.
export type DoorAction = 'check' | 'admit' | 'refuse'

interface BaseScan {
  // Made on the phone, because for a while this is the only place the scan exists.
  // Also what lets an interrupted upload be matched back to its record.
  uuid: string
  // Which pairing took this scan. Set once, at the moment it is written, and never
  // changed afterwards: it says who the scan belongs to, not where the phone is now.
  pairing: string
  code: string
  scannedAt: string
  status: ScanStatus
  attempts: number
  lastError: string | null
}

export interface LeadScan extends BaseScan {
  kind: 'lead'
  areaId: number
  // Kept alongside the id so the maintenance screen and diagnostics read sensibly
  // even for an area that has since been renamed.
  areaName: string
}

export interface DoorScan extends BaseScan {
  kind: 'door'
  sessionId: number | null
  // Kept like the area name, so the panel reads sensibly after the phone moves on.
  sessionTitle: string
  action: DoorAction
  // Null when the check never reached us.
  verdict: EntryVerdict | null
  admitted: boolean
  // False when nobody checked, which the server records as an unverified entry.
  verified: boolean
  // Resent unchanged when a decision goes up later, so it lands at the time it was
  // made. Its own uuid, because the check may already hold a row under the record's.
  decisionUuid: string | null
  decidedAt: string | null
}

export type StoredScan = LeadScan | DoorScan

// What actually comes back off the phone. A record written before door scans existed
// carries no kind, and one written before pairings were tracked carries no pairing.
// Both are normal on a phone that updated mid event.
type RawScan = Partial<StoredScan> & Omit<BaseScan, 'pairing'>

// Fills in what an older record cannot have held. A missing pairing is read as the
// legacy one rather than left blank, which is also what the upgrade writes.
const normalise = (scan: RawScan): StoredScan => ({
  ...scan,
  kind: scan.kind ?? 'lead',
  pairing: scan.pairing || LEGACY_PAIRING,
} as StoredScan)

export const isDoorScan = (scan: StoredScan): scan is DoorScan => scan.kind === 'door'

export const isLeadScan = (scan: StoredScan): scan is LeadScan => scan.kind === 'lead'

const DB_NAME = 'scan-archive'
// Raise this whenever the stored shape changes. The upgrade step below is the only
// place indexes and stores can be created, and it runs once per phone. A version is
// never lowered: a phone opening with a lower number than it already has cannot open
// its own database.
//
// 2 adds the door fields to a record. Nothing to migrate: an older record is read as
// the lead scan it was, and the indexes are unchanged.
//
// 3 fences records by pairing, and does have something to migrate. An index leaves out
// a record whose key is missing, so existing records must be stamped rather than left
// blank, or a phone updating mid shift would lose sight of its own waiting scans.
const DB_VERSION = 3
const STORE = 'scans'

let dbPromise: Promise<IDBDatabase> | null = null

const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise) {
    return dbPromise
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = request.result
      const transaction = request.transaction!

      const store = db.objectStoreNames.contains(STORE)
        ? transaction.objectStore(STORE)
        : db.createObjectStore(STORE, { keyPath: 'uuid' })

      // Uploading reads by status; the sweep, the day's count and the list read by date.
      if (!store.indexNames.contains('status')) {
        store.createIndex('status', 'status')
      }

      if (!store.indexNames.contains('scannedAt')) {
        store.createIndex('scannedAt', 'scannedAt')
      }

      // The pairing leads both keys, since every read but the stranded count asks for
      // one pairing. The plain status index stays: it counts everything waiting.
      if (!store.indexNames.contains('pairingStatus')) {
        store.createIndex('pairingStatus', ['pairing', 'status'])
      }

      if (!store.indexNames.contains('pairingScannedAt')) {
        store.createIndex('pairingScannedAt', ['pairing', 'scannedAt'])
      }

      // Records already on the phone, which predate the pairing field. Stamped with the
      // value such a phone adopts for itself, so the two agree.
      if (event.oldVersion > 0 && event.oldVersion < 3) {
        const backfill = store.openCursor()

        backfill.onsuccess = () => {
          const cursor = backfill.result

          if (!cursor) {
            return
          }

          const scan = cursor.value as RawScan

          if (!scan.pairing) {
            cursor.update({ ...scan, pairing: LEGACY_PAIRING })
          }

          cursor.continue()
        }
      }
    }

    request.onsuccess = () => {
      const db = request.result

      // Another tab wanting a newer version would otherwise be blocked by this
      // connection for as long as the page stays open.
      db.onversionchange = () => db.close()

      resolve(db)
    }

    request.onerror = () => reject(request.error)

    // Fires when an older connection is still open and the upgrade cannot run. Left
    // unhandled this neither succeeds nor fails, and every caller waits for ever.
    request.onblocked = () => reject(new Error('scan-archive upgrade blocked by another tab'))
  })

  // A failed open must not be remembered, otherwise every later call gets the same
  // rejected promise and the app cannot recover without a reload.
  dbPromise.catch(() => {
    dbPromise = null
  })

  return dbPromise
}

const withStore = async <T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDb()

  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, mode)
    const request = work(transaction.objectStore(STORE))

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

const add = async <T extends StoredScan>(scan: T): Promise<T> => {
  await withStore('readwrite', store => store.add(scan))

  return scan
}

/**
 * Writes a lead scan to the phone. Returns the stored record, whose uuid is what the
 * upload later uses to say which scans arrived.
 */
export const saveScan = (
  pairing: string,
  code: string,
  areaId: number,
  areaName: string,
): Promise<LeadScan> => add<LeadScan>({
  kind: 'lead',
  uuid: crypto.randomUUID(),
  pairing,
  code,
  areaId,
  areaName,
  scannedAt: new Date().toISOString(),
  status: 'pending',
  attempts: 0,
  lastError: null,
})

/**
 * Writes a badge held up at a door, before it is checked. The uuid it returns is the one
 * the check is sent under, so the phone's record and our row describe the same scan.
 */
export const saveDoorScan = (
  pairing: string,
  code: string,
  sessionId: number | null,
  sessionTitle: string,
): Promise<DoorScan> => add<DoorScan>({
  kind: 'door',
  uuid: crypto.randomUUID(),
  pairing,
  code,
  sessionId,
  sessionTitle,
  action: 'check',
  verdict: null,
  admitted: false,
  verified: false,
  decisionUuid: null,
  decidedAt: null,
  scannedAt: new Date().toISOString(),
  status: 'pending',
  attempts: 0,
  lastError: null,
})

export const scansByStatus = async (
  pairing: string,
  status: ScanStatus,
): Promise<StoredScan[]> => {
  const scans = await withStore(
    'readonly',
    store => store.index('pairingStatus').getAll([pairing, status]) as IDBRequest<RawScan[]>,
  )

  return scans.map(normalise)
}

export const countByStatus = (pairing: string, status: ScanStatus): Promise<number> =>
  withStore('readonly', store => store.index('pairingStatus').count([pairing, status]))

/**
 * Scans still waiting that were taken under an earlier pairing, and can never be sent.
 * Everything waiting, less what this pairing holds, which needs no index of its own.
 *
 * Both counts come from one transaction. Read separately, a scan written between them
 * would be counted on one side only and the difference could come out below zero.
 */
export const countStranded = async (pairing: string): Promise<number> => {
  const db = await openDb()

  return new Promise<number>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readonly')
    const store = transaction.objectStore(STORE)
    const everything = store.index('status').count('pending')
    const mine = store.index('pairingStatus').count([pairing, 'pending'])

    transaction.oncomplete = () => resolve(everything.result - mine.result)
    transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Every scan on the phone, whatever its pairing. What the wipe is about to delete.
 */
export const countAll = (): Promise<number> =>
  withStore('readonly', store => store.count())

/**
 * How many scans were taken since a given moment, whatever became of them. The panel's
 * day tally.
 */
export const countSince = (pairing: string, since: string): Promise<number> =>
  withStore(
    'readonly',
    // Bounded above by an empty array, which sorts after every other key type and so
    // means any timestamp. An open range would run on into the next pairing.
    store => store.index('pairingScannedAt').count(
      IDBKeyRange.bound([pairing, since], [pairing, []]),
    ),
  )

/**
 * The most recent scans, newest first. Capped, because this is read on a phone.
 */
export const recentScans = async (pairing: string, limit: number): Promise<StoredScan[]> => {
  const db = await openDb()

  return new Promise<StoredScan[]>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readonly')
    const index = transaction.objectStore(STORE).index('pairingScannedAt')
    const cursorRequest = index.openCursor(IDBKeyRange.bound([pairing], [pairing, []]), 'prev')
    const scans: StoredScan[] = []

    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result

      if (!cursor || scans.length >= limit) {
        return
      }

      scans.push(normalise(cursor.value as RawScan))
      cursor.continue()
    }

    transaction.oncomplete = () => resolve(scans)
    transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Reads a record, changes it and writes it back in one transaction, so two changes to
 * the same scan cannot lose each other.
 */
const mutate = async (uuid: string, apply: (scan: StoredScan) => void): Promise<void> => {
  const db = await openDb()

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite')
    const store = transaction.objectStore(STORE)
    const read = store.get(uuid)

    read.onsuccess = () => {
      const scan = read.result as RawScan | undefined

      if (!scan) {
        resolve()

        return
      }

      const record = normalise(scan)

      apply(record)
      store.put(record)
    }

    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Records what happened to a scan. A rejection is kept with its reason rather than
 * removed, so "I scanned that person" can always be answered.
 */
export const markScan = (
  uuid: string,
  status: ScanStatus,
  error: string | null = null,
): Promise<void> => mutate(uuid, (scan) => {
  scan.status = status
  scan.attempts += 1
  scan.lastError = error
})

/**
 * Writes down what happened at the door: what we learned, what was decided, and where
 * the record now stands, in one transaction.
 *
 * One write rather than a patch followed by markScan, so a scan cannot be left holding
 * a verdict with the wrong status if the phone dies between the two.
 *
 * `attempted` counts a request we made, which is what attempts means on a lead scan.
 * Recording a decision is not one, so it is asked for rather than assumed.
 */
export const patchDoorScan = (
  uuid: string,
  changes: Partial<Omit<DoorScan, 'uuid' | 'kind'>>,
  { attempted = false }: { attempted?: boolean } = {},
): Promise<void> => mutate(uuid, (scan) => {
  if (!isDoorScan(scan)) {
    return
  }

  Object.assign(scan, changes)

  if (attempted) {
    scan.attempts += 1
  }
})

/**
 * Notes a failed attempt without settling the scan. A transport failure is not the
 * scan's fault and must not count as a rejection.
 */
export const noteAttempt = (uuid: string, error: string): Promise<void> =>
  markScan(uuid, 'pending', error)

/**
 * Removes every record taken under a different pairing, and returns how many went.
 *
 * Narrower than the wipe below on purpose: it cannot touch the work of the pairing the
 * phone is on now, so it is safe to offer while a shift is running.
 *
 * @returns how many records were removed
 */
export const wipeOtherPairings = async (pairing: string): Promise<number> => {
  const db = await openDb()

  return new Promise<number>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite')
    const cursorRequest = transaction.objectStore(STORE).openCursor()
    let removed = 0

    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result

      if (!cursor) {
        return
      }

      // A record with no pairing at all is not this one's either.
      if ((cursor.value as RawScan).pairing !== pairing) {
        cursor.delete()
        removed++
      }

      cursor.continue()
    }

    transaction.oncomplete = () => resolve(removed)
    transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Removes everything. One of only three ways a scan leaves the phone, alongside a
 * wipe we request from the server and the retention ceiling.
 */
export const wipeScans = (): Promise<void> =>
  withStore('readwrite', store => store.clear())

/**
 * Removes scans older than the retention window, whatever their status, kind or
 * pairing. Deliberately blind to the pairing: the promise was made about everything the
 * phone holds, and a fenced record must not outlive it by being invisible.
 *
 * Not optional, and not engineering taste. Ticket codes with timestamps are data we
 * can trace back to a person, and the signed agreement commits us to deleting lead
 * data within 30 days of a request. A phone that never comes back online can only
 * honour that with a clock running on the phone itself.
 *
 * @returns how many records were removed
 */
export const purgeOlderThan = async (retentionDays: number): Promise<number> => {
  const cutoff = new Date(Date.now() - retentionDays * 24 * 60 * 60 * 1000).toISOString()
  const db = await openDb()

  return new Promise<number>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite')
    const index = transaction.objectStore(STORE).index('scannedAt')
    const cursorRequest = index.openCursor(IDBKeyRange.upperBound(cutoff))
    let removed = 0

    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result

      if (!cursor) {
        return
      }

      cursor.delete()
      removed++
      cursor.continue()
    }

    transaction.oncomplete = () => resolve(removed)
    transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * How much room the app has. Surfaced in the maintenance panel and the heartbeat,
 * so a phone approaching its limit is visible before it starts failing.
 */
export const storageEstimate = async (): Promise<{ usage: number, quota: number } | null> => {
  if (!navigator.storage?.estimate) {
    return null
  }

  const { usage = 0, quota = 0 } = await navigator.storage.estimate()

  return { usage, quota }
}
